import { TableConfig } from '~/classes/constants/table-format.constants';

export interface MethodologyNote {
    label: string;
    value: string;
}

export interface GuideIntroContent {
    riskScoreFormula: string;
    riskScoreDetails: string;
    badActorCandidacyRule: string;
    criticalitySource: string;
    guardrails: string;
}

export interface GuideStatusRow {
    status: string;
    condition: string;
    tone: 'new' | 'worsening' | 'improving' | 'recurring' | 'stable' | 'resolved';
}

export const GUIDE_PAGE_TITLE = 'Methodology & Guardrails';

export const GUIDE_INTRO_CONTENT: GuideIntroContent = {
    riskScoreFormula: 'Score = 0.50-CI + 0.375-Crit + 0.125-DB',
    riskScoreDetails: 'CI bands: 0-2 WOs→0.5 · 3-4 WOs→0.75 · 5+→1.0  |  Criticality (MAXIMO/RIMS): H→1.0 · M→0.75 · L→0.0  |  DB: 1 source→0.5 · 2-3→0.75 · 4+→1.0',
    badActorCandidacyRule: '(≥ 2 similar CM WOs/12 months OR ≥ 3 different CM WOs/12 months) AND total CM cost > $50,000. "Similar" = CM work orders sharing the same normalised failure description; "different" = all distinct CM WOs. WO counts are evaluated over the trailing 1-year window of Reported Date entries for the asset. Cost sourced from RIMS (SAP Amount; Maximo Cost column used as fallback).',
    criticalitySource: 'MAXIMO (primary) → RIMS (fallback) — never LLM-assigned. Risk Type (LPO/Safety/Environmental): LLM-classified based on evidence.',
    guardrails: 'SENTRY uses leading indicators only. Lagging indicators (COI, OA%, bad actor lists) are not used as primary signals. SENTRY does not perform root cause analysis or prescribe corrective actions. All narratives are grounded on cited source records.'
};

export const GUIDE_CANONICAL_FORMULA = [
    'Score = 0.50 × CI + 0.375 × Crit + 0.125 × DB   (computed on a 0–1 scale)',
    '',
    'CI   = Count of incidents (Mentions from all sources) frequency band: 0-2 -> 0.50   3-4 -> 0.75   >=5 -> 1.00',
    'Crit = Criticality band: H -> 1.00   M -> 0.75   L/N -> 0.00',
    'DB   = Source diversity band: 1 source -> 0.50   2-3 -> 0.75   >=4 -> 1.00',
    "'Plant' not taken into account for Risk score calculation",
    "The 0–1 Score above is scaled ×100 before banding:",
    'Band thresholds: >=70 -> RED   >=40 -> AMBER   <40 -> GREEN'
];

export const GUIDE_SCORING_REFERENCE_TITLE = 'Scoring Methodology Reference';

export const GUIDE_SCORING_REFERENCE_TEXT = 'Every number in this report is computed deterministically from source data. The LLM explains scores - it never invents them. Two scores are computed per asset.';

export const GUIDE_CANONICAL_TITLE = '1 - Canonical Rank Score (4-factor, drives ranking & band colour)';

export const GUIDE_AI_ADJUSTED_TITLE = '2 - AI-Adjusted Risk Score (semantic correction, for monitoring only)';

export const GUIDE_EXPLAINABILITY_TITLE = '3 - Explainability Score (7-factor, 0-100 - reasoning card only, NOT used for ranking)';

export const GUIDE_RISK_TREND_TITLE = '4 - Risk Trend (MoM)';

export const GUIDE_RISK_TREND_NOTE_LINE2_PREFIX = 'If no prior-month data exists, all assets show';

export const GUIDE_AI_ADJUSTED_CONTENT = [
    'If band(D) == band(L):',
    '    Adjusted = D',
    '',
    'Else:',
    '    Adjusted = D + α(L - D)',
    '',
    'D = Normalized deterministic risk (RiskScore / 100)       ∈ [0, 1]',
    'L = Semantic risk score (LLM judge)                       ∈ {0.1, 0.3, 0.5, 0.7, 0.9}',
    'α = AI adjustment strength (default 0.5)',
    '',
    'Semantic risk scale:',
    '0.1 = No meaningful reliability concern / largely benign references',
    '0.3 = Weak evidence of concern',
    '0.5 = Mixed, ambiguous or moderate concern',
    '0.7 = Significant reliability concern',
    '0.9 = Strong and consistent evidence of significant reliability risk',
    '',
    'Risk bands (same for D and L):',
    'LOW    : 0.00 <= score < 0.40',
    'MEDIUM : 0.40 <= score < 0.70',
    'HIGH   : 0.70 <= score <= 1.00',
    '',
    'Adjustment only applied when D and L are in different bands.',
    'The LLM judge consumes consolidated equipment narrative (main_concern, recommended_action, trend_selected_month, trend_last_6_months,',
    'preventive_maintenance, open_work_orders, risk and failure type) - NOT raw source documents.',
    'D, L and Adjusted are all expressed on a 0–1 scale. The AI-Adjusted Risk',
    'Score reported to users is Adjusted x 100 (rounded), matching the same',
    '0-100 scale used by the Canonical Rank Score'
];

export const GUIDE_EXPLAINABILITY_FORMULA = [
    'Expl. Score = 100 * (0.25*criticality + 0.20*severity + 0.15*recurrence',
    '                    + 0.15*recency + 0.10*source_diversity',
    '                    + 0.10*unresolved + 0.05*trend_worsening)'
];

export const GUIDE_FEATURE_TABLE: TableConfig = {
    columns: [
        { key: 'feature', label: 'Feature' },
        { key: 'weight', label: 'Weight' },
        { key: 'normalization', label: 'Normalisation formula (output\n0-1)' },
        { key: 'capture', label: 'What it captures' }
    ],
    rows: [
        {
            feature: 'criticality',
            weight: '0.25',
            normalization: 'min(crit_int, 4) ÷ 4  (N=1, L=2, M=3, H=4)',
            capture: 'Asset consequence severity'
        },
        {
            feature: 'severity',
            weight: '0.20',
            normalization: 'Averaged 0-1 from 5 independent LLM classify calls',
            capture: 'Worst observed symptom severity'
        },
        {
            feature: 'recurrence',
            weight: '0.15',
            normalization: 'min(total_mentions, 6) ÷ 6',
            capture: 'How many times the asset appears this month'
        },
        {
            feature: 'recency',
            weight: '0.15',
            normalization: 'max(0, 1 - days_since_latest_signal ÷ 30)',
            capture: 'How recent the last signal is'
        },
        {
            feature: 'source_diversity',
            weight: '0.10',
            normalization: 'min(distinct_sources, 4) ÷ 4',
            capture: 'Cross-system corroboration'
        },
        {
            feature: 'unresolved',
            weight: '0.10',
            normalization: '1.0 if any MAXIMO WO is open/unclosed, else 0.0',
            capture: 'Risk still active with no resolution'
        },
        {
            feature: 'trend_worsening',
            weight: '0.05',
            normalization: 'If prior-month history exists:\nmin(1, (this_score - prior_score) ÷ 20)\nOtherwise (first run): LLM classification trend hint (0-1) boosted by chronology pattern\npost_maintenance_recurrence -> 1.0\nworsening -> 0.7\nother -> LLM value',
            capture: 'Month-over-month deterioration'
        }
    ]
};

export const GUIDE_RISK_TREND_TABLE: TableConfig<GuideStatusRow> = {
    columns: [
        { key: 'status', label: 'Status' },
        { key: 'condition', label: 'Condition' }
    ],
    rows: [
        {
            status: 'NEW',
            condition: 'Asset not in prior month, and current band ≠ green',
            tone: 'new'
        },
        {
            status: 'WORSENING',
            condition: 'Canonical score increased >= 5 pts vs prior month',
            tone: 'worsening'
        },
        {
            status: 'IMPROVING',
            condition: 'Canonical score decreased >= 5 pts vs prior month',
            tone: 'improving'
        },
        {
            status: 'RECURRING',
            condition: 'Still non-green, score change < 5 pts',
            tone: 'recurring'
        },
        {
            status: 'STABLE',
            condition: 'Green band both months (or new asset, green now)',
            tone: 'stable'
        },
        {
            status: 'RESOLVED',
            condition: 'Was non-green last month, now green',
            tone: 'resolved'
        }
    ]
};